(() => {
  const rootSelector = '[data-kqs-event-calendar]';
  const viewButtonSelector = '[data-kqs-calendar-view]';
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function parseDate(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    const date = new Date(year, month, day);

    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
    return date;
  }

  function isoDate(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  function dayDifference(start, end) {
    const startUtc = Date.UTC(start.getFullYear(), start.getMonth(), start.getDate());
    const endUtc = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
    return Math.round((endUtc - startUtc) / 86400000);
  }

  function longDate(date) {
    return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }

  function sourceIncludesDate(source, selectedDate) {
    const start = parseDate(source.dataset.eventDate);
    if (!start) return false;
    const parsedEnd = parseDate(source.dataset.eventEndDate);
    const end = parsedEnd && parsedEnd >= start ? parsedEnd : start;
    return selectedDate >= start && selectedDate <= end;
  }

  function selectDate(root, date, cell) {
    const detail = root.querySelector('[data-kqs-date-detail]');
    const heading = root.querySelector('[data-kqs-date-detail-heading]');
    const content = root.querySelector('[data-kqs-date-detail-content]');
    if (!detail || !heading || !content) return;

    const sources = Array.from(root.querySelectorAll('[data-kqs-date-detail-source]'))
      .filter((source) => sourceIncludesDate(source, date));
    if (!sources.length) return;

    content.replaceChildren();
    sources.forEach((source) => {
      Array.from(source.children).forEach((child) => content.appendChild(child.cloneNode(true)));
    });

    const state = root.kqsMonthCalendar;
    if (state && state.selectedCell) {
      state.selectedCell.classList.remove('kqs-month-calendar__day--selected');
      state.selectedCell.setAttribute('aria-selected', 'false');
    }
    if (state) state.selectedCell = cell;

    cell.classList.add('kqs-month-calendar__day--selected');
    cell.setAttribute('aria-selected', 'true');
    heading.textContent = `Selected date — ${longDate(date)}`;
    detail.hidden = false;

    const prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (typeof detail.scrollIntoView === 'function') {
      detail.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
    }
    if (typeof heading.focus === 'function') heading.focus({ preventScroll: true });
  }

  function readEvents(root) {
    return Array.from(root.querySelectorAll('[data-kqs-calendar-event]'))
      .map((item) => {
        const date = parseDate(item.dataset.eventDate);
        if (!date) return null;
        const parsedEndDate = parseDate(item.dataset.eventEndDate);
        const endDate = parsedEndDate && parsedEndDate >= date ? parsedEndDate : date;

        return {
          city: item.dataset.eventCity || '',
          date,
          endDate,
          link: item.dataset.eventLink || '',
          sequence: Number(item.dataset.eventSequence) || 0,
          status: item.dataset.eventStatus || 'upcoming',
          statusLabel: item.dataset.eventStatusLabel || 'Upcoming',
          time: item.dataset.eventTime || '',
          title: item.dataset.eventTitle || 'Scheduled event',
          total: Number(item.dataset.eventTotal) || 0
        };
      })
      .filter(Boolean)
      .sort((left, right) => left.date - right.date);
  }

  function createEventEntry(event, displayedDate) {
    const wrapper = document.createElement('div');
    wrapper.className = 'kqs-month-calendar__event';
    const duration = dayDifference(event.date, event.endDate) + 1;
    const spanDay = dayDifference(event.date, displayedDate) + 1;
    const continuesBefore = displayedDate > event.date;
    const continuesAfter = displayedDate < event.endDate;
    const startsVisibleSegment = !continuesBefore || displayedDate.getDay() === 0 || displayedDate.getDate() === 1;
    const occurrenceText = event.sequence > 0 && event.total > 1 ? `, occurrence ${event.sequence} of ${event.total}` : '';

    if (duration > 1) {
      wrapper.classList.add('kqs-month-calendar__event--multi-day');
      if (continuesBefore) wrapper.classList.add('kqs-month-calendar__event--continues-before');
      if (continuesAfter) wrapper.classList.add('kqs-month-calendar__event--continues-after');
      wrapper.setAttribute('aria-label', `${event.title}${occurrenceText}, day ${spanDay} of ${duration}, ${event.statusLabel}`);
    } else if (occurrenceText) {
      wrapper.setAttribute('aria-label', `${event.title}${occurrenceText}, ${event.statusLabel}`);
    }

    if (duration === 1 || startsVisibleSegment) {
      const labelRow = document.createElement('div');
      labelRow.className = 'kqs-month-calendar__label-row';

      const badge = document.createElement('span');
      badge.className = 'product-label kqs-event-status kqs-month-calendar__status';
      badge.dataset.eventStatus = event.status;
      badge.textContent = event.statusLabel;
      labelRow.appendChild(badge);

      if (event.sequence > 0 && event.total > 1) {
        const sequence = document.createElement('span');
        sequence.className = 'product-label kqs-event-sequence-label';
        sequence.textContent = `${event.sequence}/${event.total}`;
        labelRow.appendChild(sequence);
      }

      wrapper.appendChild(labelRow);
    }

    const title = document.createElement(event.link ? 'a' : 'span');
    title.className = event.link ? 'kqs-month-calendar__event-title link text--strong' : 'kqs-month-calendar__event-title text--strong';
    title.textContent = event.title;
    if (event.link) title.href = event.link;
    wrapper.appendChild(title);

    const metaParts = [];
    if (!continuesBefore) metaParts.push(event.time, event.city);
    if (duration > 1) metaParts.push(`Day ${spanDay} of ${duration}`);
    const compactMeta = metaParts.filter(Boolean);
    if (compactMeta.length) {
      const meta = document.createElement('span');
      meta.className = 'kqs-month-calendar__event-meta';
      meta.textContent = compactMeta.join(' · ');
      wrapper.appendChild(meta);
    }

    return wrapper;
  }

  function initialMonth(events) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextEvent = events.find((event) => event.endDate >= today);
    const reference = nextEvent ? nextEvent.date : (events.length ? events[events.length - 1].date : today);
    return new Date(reference.getFullYear(), reference.getMonth(), 1);
  }

  function renderMonth(root) {
    const state = root.kqsMonthCalendar;
    if (!state) return;

    const { cursor, daysRoot, emptyMessage, events, label } = state;
    const year = cursor.getFullYear();
    const month = cursor.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cellCount = Math.ceil((firstWeekday + daysInMonth) / 7) * 7;
    const todayKey = isoDate(new Date());
    const eventsByDate = new Map();

    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);

    events.forEach((event) => {
      if (event.endDate < monthStart || event.date > monthEnd) return;
      const cursorDate = new Date(Math.max(event.date.getTime(), monthStart.getTime()));
      const lastDate = new Date(Math.min(event.endDate.getTime(), monthEnd.getTime()));

      while (cursorDate <= lastDate) {
        const dateKey = isoDate(cursorDate);
        const current = eventsByDate.get(dateKey) || [];
        current.push(event);
        eventsByDate.set(dateKey, current);
        cursorDate.setDate(cursorDate.getDate() + 1);
      }
    });

    label.textContent = `${monthNames[month]} ${year}`;
    daysRoot.replaceChildren();

    let week = null;
    for (let index = 0; index < cellCount; index += 1) {
      if (index % 7 === 0) {
        week = document.createElement('div');
        week.className = 'kqs-month-calendar__week';
        week.setAttribute('role', 'row');
        daysRoot.appendChild(week);
      }

      const dayNumber = index - firstWeekday + 1;
      const cell = document.createElement('div');
      cell.className = 'kqs-month-calendar__day';
      cell.setAttribute('role', 'gridcell');

      if (dayNumber < 1 || dayNumber > daysInMonth) {
        cell.classList.add('kqs-month-calendar__day--empty');
        cell.setAttribute('aria-hidden', 'true');
        week.appendChild(cell);
        continue;
      }

      const date = new Date(year, month, dayNumber);
      const dateKey = isoDate(date);
      const dayEvents = eventsByDate.get(dateKey) || [];
      cell.setAttribute('aria-label', `${dayNames[date.getDay()]}, ${monthNames[month]} ${dayNumber}, ${year}${dayEvents.length ? `, ${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}, select to view details` : ''}`);
      if (dateKey === todayKey) cell.classList.add('kqs-month-calendar__day--today');

      if (dayEvents.length) {
        cell.classList.add('kqs-month-calendar__day--selectable');
        cell.tabIndex = 0;
        const detail = root.querySelector('[data-kqs-date-detail]');
        if (detail && detail.id) cell.setAttribute('aria-controls', detail.id);
        cell.setAttribute('aria-selected', 'false');
        cell.addEventListener('click', (event) => {
          if (event.target.closest && event.target.closest('a')) return;
          selectDate(root, date, cell);
        });
        cell.addEventListener('keydown', (event) => {
          if (event.target.closest && event.target.closest('a')) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          selectDate(root, date, cell);
        });
      }

      const number = document.createElement('span');
      number.className = 'kqs-month-calendar__day-number text--strong';
      number.textContent = String(dayNumber);
      cell.appendChild(number);

      dayEvents.forEach((event) => cell.appendChild(createEventEntry(event, date)));
      week.appendChild(cell);
    }

    emptyMessage.hidden = eventsByDate.size > 0;
  }

  function bindMonthGrid(root) {
    const monthRoot = root.querySelector('[data-kqs-month-calendar]');
    if (!monthRoot) return;

    const events = readEvents(root);
    root.kqsMonthCalendar = {
      cursor: initialMonth(events),
      daysRoot: monthRoot.querySelector('[data-kqs-calendar-days]'),
      emptyMessage: root.querySelector('[data-kqs-month-empty]'),
      events,
      label: monthRoot.querySelector('[data-kqs-month-label]'),
      selectedCell: null
    };

    monthRoot.querySelector('[data-kqs-month-previous]').addEventListener('click', () => {
      const state = root.kqsMonthCalendar;
      state.cursor = new Date(state.cursor.getFullYear(), state.cursor.getMonth() - 1, 1);
      renderMonth(root);
    });

    monthRoot.querySelector('[data-kqs-month-next]').addEventListener('click', () => {
      const state = root.kqsMonthCalendar;
      state.cursor = new Date(state.cursor.getFullYear(), state.cursor.getMonth() + 1, 1);
      renderMonth(root);
    });

    renderMonth(root);
  }

  function activateView(root, view) {
    root.dataset.view = view;
    root.querySelectorAll(viewButtonSelector).forEach((button) => {
      const isActive = button.dataset.kqsCalendarView === view;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.classList.toggle('button--primary', isActive);
      button.classList.toggle('button--ternary', !isActive);
    });
    root.querySelectorAll('[data-kqs-calendar-panel]').forEach((panel) => {
      panel.setAttribute('aria-hidden', panel.dataset.kqsCalendarPanel === view ? 'false' : 'true');
    });
  }

  function getInitialView(root) {
    const configuredView = root.dataset.view || 'calendar';
    const mobileListEnabled = root.dataset.mobileDefaultView === 'list';
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 640px)').matches;

    return mobileListEnabled && isMobile ? 'list' : configuredView;
  }

  function bindCalendar(root) {
    if (root.dataset.kqsCalendarBound === 'true') return;
    root.dataset.kqsCalendarBound = 'true';

    bindMonthGrid(root);
    activateView(root, getInitialView(root));

    root.querySelectorAll(viewButtonSelector).forEach((button) => {
      button.addEventListener('click', () => {
        activateView(root, button.dataset.kqsCalendarView || 'calendar');
      });
    });
  }

  function bindAll() {
    document.querySelectorAll(rootSelector).forEach(bindCalendar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll, { once: true });
  } else {
    bindAll();
  }

  document.addEventListener('shopify:section:load', bindAll);
})();
